import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Typography,
  Box,
  CircularProgress,
  Paper,
  Chip,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Search as SearchIcon,
  Description as QuoteIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  ShoppingCart as CartIcon,
  Send as SendIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
} from "@material-ui/icons";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "react-toastify";
import api from "../../services/api";
import * as pharmaApi from "../../services/pharmaApi";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minHeight: 400,
    padding: theme.spacing(2),
  },
  searchSection: {
    marginBottom: theme.spacing(2),
  },
  filtersRow: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    flexWrap: "wrap",
  },
  filterField: {
    minWidth: 120,
  },
  quotesList: {
    maxHeight: 350,
    overflowY: "auto",
  },
  quoteItem: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    cursor: "pointer",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.primary.main,
    },
  },
  quoteItemSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
  },
  quoteHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
  },
  quoteNumber: {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  quoteCustomer: {
    fontSize: "0.9rem",
    color: theme.palette.text.primary,
  },
  quoteInfo: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
  },
  quoteTotal: {
    fontWeight: 600,
    color: theme.palette.success.main,
    fontSize: "1rem",
  },
  statusChip: {
    height: 22,
    fontSize: "0.7rem",
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  totalizerSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[100],
    borderRadius: 8,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  totalizerItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: 80,
  },
  totalizerLabel: {
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
  },
  totalizerValue: {
    fontSize: "1rem",
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  totalizerTotal: {
    fontSize: "1.2rem",
    fontWeight: 700,
    color: theme.palette.success.main,
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  actionButton: {
    fontSize: "0.75rem",
    padding: theme.spacing(0.5, 1),
  },
  itemsPreview: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
}));

const STATUS_CONFIG = {
  draft: { label: "Rascunho", color: "default", icon: ScheduleIcon },
  sent: { label: "Enviado", color: "primary", icon: SendIcon },
  viewed: { label: "Visualizado", color: "primary", icon: CheckCircleIcon },
  accepted: { label: "Aceito", color: "primary", icon: CheckCircleIcon },
  rejected: { label: "Rejeitado", color: "secondary", icon: CancelIcon },
  expired: { label: "Expirado", color: "secondary", icon: WarningIcon },
  converted: { label: "Convertido", color: "default", icon: CheckCircleIcon },
  cancelled: { label: "Cancelado", color: "default", icon: CancelIcon },
  // Legacy fallback
  active: { label: "Ativo", color: "primary", icon: ScheduleIcon },
};

// Status that allow actions (recover, send, cancel)
const ACTIVE_STATUSES = ["draft", "sent", "viewed", "accepted", "active"];

const QuoteSearchModal = ({ open, onClose, onRecover, onSend, ticketId, ticketUuid, contactPhone }) => {
  const classes = useStyles();

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);

  const formatPrice = (price) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(price || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return format(parseISO(dateStr), "dd/MM/yyyy HH:mm", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  // Calculate totals for displayed quotes
  const calculateTotals = useCallback(() => {
    if (!quotes || quotes.length === 0) {
      return { count: 0, total: 0, pending: 0, converted: 0 };
    }

    const total = quotes.reduce((sum, q) => sum + (parseFloat(q.total) || 0), 0);
    const pending = quotes.filter(q =>
      ACTIVE_STATUSES.includes(q.status) &&
      !(q.validUntil && new Date(q.validUntil) < new Date())
    ).length;
    const converted = quotes.filter(q => q.status === "converted").length;

    return {
      count: quotes.length,
      total,
      pending,
      converted,
    };
  }, [quotes]);

  const totals = calculateTotals();

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        pageNumber: "1",
        status: statusFilter || undefined,
      };

      if (searchQuery.trim()) {
        // Check if search is phone, CPF, or general
        const digits = searchQuery.replace(/\D/g, "");
        if (digits.length === 11 && !searchQuery.includes(" ")) {
          params.cpf = digits;
        } else if (digits.length >= 8 && digits.length <= 11) {
          params.phone = digits;
        } else {
          params.searchParam = searchQuery;
        }
      }

      // If we have contactPhone, search by it
      if (contactPhone && !searchQuery.trim()) {
        params.phone = contactPhone.replace(/\D/g, "");
      }

      // Use PharmaFlow API
      const response = await pharmaApi.searchQuotes(params);
      // API returns { data: [...], total, page, ... }
      setQuotes(response.data || response.quotes || response || []);
    } catch (err) {
      console.error("Error fetching quotes:", err);
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, contactPhone]);

  useEffect(() => {
    if (open) {
      fetchQuotes();
    }
  }, [open, fetchQuotes]);

  const handleRecover = async (quote) => {
    try {
      // Get full quote with items from PharmaFlow
      const data = await pharmaApi.getQuote(quote.id);

      const isExpired = data.status === "expired" ||
        (data.validUntil && new Date(data.validUntil) < new Date());

      if (isExpired) {
        toast.warning("Este orçamento está expirado. Os preços podem ter mudado.");
      }

      if (onRecover) {
        onRecover(data, data.items || []);
      }
      onClose();
      toast.success(`Orçamento ${quote.quoteNumber} recuperado!`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleSend = async (quote) => {
    try {
      // Use PharmaFlow API to send quote with ticketUuid for interactive buttons
      const data = await pharmaApi.sendQuoteToCustomer(quote.id, ticketId, ticketUuid);

      if (onSend && data.message) {
        // Use ticketUuid for Whaticket message API
        onSend(data.message, ticketUuid);
      }

      toast.success("Orçamento enviado ao cliente com botões interativos!");
      fetchQuotes();
    } catch (err) {
      toastError(err);
    }
  };

  const handleCancel = async (quote) => {
    if (!window.confirm(`Cancelar orçamento ${quote.quoteNumber}?`)) {
      return;
    }

    try {
      // Use PharmaFlow API to cancel quote
      await pharmaApi.cancelQuote(quote.id);
      toast.success("Orçamento cancelado!");
      fetchQuotes();
    } catch (err) {
      toastError(err);
    }
  };

  const renderQuoteItem = (quote) => {
    const config = STATUS_CONFIG[quote.status] || STATUS_CONFIG.draft;
    const StatusIcon = config.icon;
    const itemsCount = quote.items?.length || 0;
    // Check if quote is active (can perform actions) - use validUntil or expiresAt
    const expirationDate = quote.validUntil || quote.expiresAt;
    const isExpired = expirationDate && new Date(expirationDate) < new Date();
    const isActiveStatus = ACTIVE_STATUSES.includes(quote.status);

    return (
      <Paper
        key={quote.id}
        className={`${classes.quoteItem} ${selectedQuote?.id === quote.id ? classes.quoteItemSelected : ""}`}
        elevation={0}
        onClick={() => setSelectedQuote(quote)}
      >
        <div className={classes.quoteHeader}>
          <Box display="flex" alignItems="center" gap={1}>
            <QuoteIcon fontSize="small" color="primary" />
            <Typography className={classes.quoteNumber}>
              {quote.quoteNumber}
            </Typography>
            <Chip
              size="small"
              label={isExpired ? "Expirado" : config.label}
              color={isExpired ? "secondary" : config.color}
              icon={<StatusIcon style={{ fontSize: 14 }} />}
              className={classes.statusChip}
            />
          </Box>
          <Typography className={classes.quoteTotal}>
            {formatPrice(quote.total)}
          </Typography>
        </div>

        <Typography className={classes.quoteCustomer}>
          {quote.customerName || "Cliente não identificado"}
        </Typography>

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography className={classes.quoteInfo}>
            {formatDate(quote.createdAt)} | {itemsCount} {itemsCount === 1 ? "item" : "itens"}
            {expirationDate && !isExpired && isActiveStatus && (
              <> | Válido até {formatDate(expirationDate)}</>
            )}
          </Typography>
        </Box>

        {quote.items && quote.items.length > 0 && (
          <Typography className={classes.itemsPreview}>
            {quote.items.slice(0, 3).map((item, idx) => {
              const snapshot = JSON.parse(item.productSnapshot || "{}");
              return snapshot.name;
            }).join(", ")}
            {quote.items.length > 3 && ` +${quote.items.length - 3} mais`}
          </Typography>
        )}

        {isActiveStatus && !isExpired && (
          <div className={classes.actionButtons}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              className={classes.actionButton}
              startIcon={<CartIcon style={{ fontSize: 14 }} />}
              onClick={(e) => {
                e.stopPropagation();
                handleRecover(quote);
              }}
            >
              Recuperar
            </Button>
            {ticketUuid && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                className={classes.actionButton}
                startIcon={<SendIcon style={{ fontSize: 14 }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSend(quote);
                }}
              >
                Enviar
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              className={classes.actionButton}
              startIcon={<CancelIcon style={{ fontSize: 14 }} />}
              onClick={(e) => {
                e.stopPropagation();
                handleCancel(quote);
              }}
            >
              Cancelar
            </Button>
          </div>
        )}

        {(isExpired || quote.status === "expired" || quote.status === "rejected") && !isActiveStatus && (
          <div className={classes.actionButtons}>
            <Button
              size="small"
              variant="outlined"
              color="default"
              className={classes.actionButton}
              startIcon={<CartIcon style={{ fontSize: 14 }} />}
              onClick={(e) => {
                e.stopPropagation();
                handleRecover(quote);
              }}
            >
              Recuperar (preços podem ter mudado)
            </Button>
          </div>
        )}

        {isActiveStatus && isExpired && (
          <div className={classes.actionButtons}>
            <Button
              size="small"
              variant="outlined"
              color="default"
              className={classes.actionButton}
              startIcon={<CartIcon style={{ fontSize: 14 }} />}
              onClick={(e) => {
                e.stopPropagation();
                handleRecover(quote);
              }}
            >
              Recuperar (expirado - preços podem ter mudado)
            </Button>
          </div>
        )}
      </Paper>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <QuoteIcon color="primary" />
            <Typography variant="h6">Orçamentos</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Atualizar">
              <IconButton size="small" onClick={fetchQuotes}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <div className={classes.searchSection}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Buscar por número, cliente, telefone ou CPF..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && fetchQuotes()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <div className={classes.filtersRow}>
            <FormControl variant="outlined" size="small" className={classes.filterField}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="draft">Rascunho</MenuItem>
                <MenuItem value="sent">Enviado</MenuItem>
                <MenuItem value="viewed">Visualizado</MenuItem>
                <MenuItem value="accepted">Aceito</MenuItem>
                <MenuItem value="rejected">Rejeitado</MenuItem>
                <MenuItem value="converted">Convertido</MenuItem>
                <MenuItem value="expired">Expirado</MenuItem>
                <MenuItem value="cancelled">Cancelado</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="contained"
              color="primary"
              onClick={fetchQuotes}
              startIcon={<SearchIcon />}
            >
              Buscar
            </Button>
          </div>
        </div>

        <Divider style={{ marginBottom: 16 }} />

        <div className={classes.quotesList}>
          {loading ? (
            <div className={classes.loadingContainer}>
              <CircularProgress />
            </div>
          ) : quotes.length > 0 ? (
            quotes.map(renderQuoteItem)
          ) : (
            <div className={classes.emptyState}>
              <QuoteIcon style={{ fontSize: 48, opacity: 0.3 }} />
              <Typography variant="body1">Nenhum orçamento encontrado</Typography>
              <Typography variant="body2">
                Tente ajustar os filtros de busca
              </Typography>
            </div>
          )}
        </div>

        {/* Totalizer Section */}
        {!loading && quotes.length > 0 && (
          <div className={classes.totalizerSection}>
            <div className={classes.totalizerItem}>
              <Typography className={classes.totalizerLabel}>Qtd</Typography>
              <Typography className={classes.totalizerValue}>{totals.count}</Typography>
            </div>
            <div className={classes.totalizerItem}>
              <Typography className={classes.totalizerLabel}>Pendentes</Typography>
              <Typography className={classes.totalizerValue}>{totals.pending}</Typography>
            </div>
            <div className={classes.totalizerItem}>
              <Typography className={classes.totalizerLabel}>Convertidos</Typography>
              <Typography className={classes.totalizerValue}>{totals.converted}</Typography>
            </div>
            <div className={classes.totalizerItem}>
              <Typography className={classes.totalizerLabel}>Total Geral</Typography>
              <Typography className={classes.totalizerTotal}>{formatPrice(totals.total)}</Typography>
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuoteSearchModal;
